import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employeesCollection from 'modules/employees/store/collections/employees'
import { Store } from 'modules/locations/components/AddEmployee/Store'

interface Props {
  locationId: number
  onClose: () => void
  contractVersions: ContractVersions
  loading: boolean
}

const AddEmployee = ({ onClose, locationId, contractVersions, loading }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const store = new Store(locationId)
  const location = store.location
  const employees = store.employees
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState<number[]>([])

  const onSelectEmployee = (employeeId: number | null | undefined) => {
    if (!employeeId) {
      setSelectedEmployeeIds([])

      return
    }

    setSelectedEmployeeIds(xor(selectedEmployeeIds, [employeeId]))
  }

  const onSelectEmployees = (ids: number[]) => {
    setSelectedEmployeeIds(ids)
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!location) return null

    const promises = selectedEmployeeIds.map((id) => {
      const employee = employeesCollection.get(id)

      if (!employee) return null

      return employee.save({
        location_id: location.attributes.id,
      })
    })

    const { success } = await asyncAction(() => Promise.all(promises))

    if (success) {
      closePortal()
    } else {
      // Workaround for a more descriptive error until product handles the issue with the manager permissions
      app.setFlash({
        type: 'wrong',
        message: i18n.t('settings.locations.location.employees.add.error_permissions'),
        time: 3000,
      })
    }
  }

  const canManageLocation = users.current.policy.can('locations.locationManage')

  if (!location || !employees) return null

  return (
    <AuthorizeUser authorizeFn={() => canManageLocation}>
      <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
        <Modal
          illustration="user-male-female"
          title={i18n.t('settings.locations.location.employees.add.title', {
            name: location.attributes.name,
          })}
          description={i18n.t('settings.locations.location.employees.add.description')}
          onClose={onClose}
          onSubmit={onSubmit}
          footer={
            <Button disabled={!selectedEmployeeIds.length} submit>
              {i18n.t('settings.locations.location.employees.add.submit')}
            </Button>
          }
        >
          {loading ? (
            <Loading />
          ) : (
            <EmployeeSelector
              employees={employees}
              onSelectEmployee={onSelectEmployee}
              onSelectMultiple={onSelectEmployees}
              selectedEmployeeIds={selectedEmployeeIds}
              contractVersions={contractVersions}
            />
          )}
        </Modal>
      </ExpiredTrialChecker>
    </AuthorizeUser>
  )
}

export default observer(AddEmployee)
